import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { Checkbox } from '@/common/ui/checkbox'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Badge } from '@/common/ui/badge'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Send, Calendar, Eye, Copy, Download, CheckCircle2 } from 'lucide-react'
import { DateTimePicker } from '@/common/components/DateTimePicker'
import { toast } from 'sonner'
import useMutationCreateMessage from '../../api/hooks/useMutationCreateMessage'
import { ChannelSelector } from '../ChannelSelector'
import { SMSSettingsForm } from '../SMSSettingsForm'
import { LINESettingsForm } from '../LINESettingsForm'
import { FlexMessageSelector } from '../FlexMessageSelector'
import { PromotionSelector } from '../PromotionSelector'
import {
	MessageChannel,
	MessageTransactionType,
	MessageCondition,
	GetPlayerByConditionRequest,
} from '../../types'
import { calculateCredit, getLanguageLabel } from '../../utils/smsCredits'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

import type { CreditData } from '../../utils/smsCredits'

const messageFormSchema = z
	.object({
		sendType: z.nativeEnum(MessageTransactionType),
		channels: z
			.array(z.nativeEnum(MessageChannel))
			.min(1, 'กรุณาเลือกอย่างน้อย 1 ช่องทาง'),
		providerId: z.string().optional(),
		senderId: z.string().optional(),
		message: z.string().optional(),
		scheduledAt: z.date().optional(),
		hotGameFlexMessageId: z.string().optional(),
		promotionId: z.string().optional(),
		enableInstantClaim: z.boolean().optional(),
	})
	.refine(
		(data) => {
			// Message is required when not using LINE_HOT_GAME or LINE_PROMOTION
			if (
				!data.channels.includes(MessageChannel.LINE_HOT_GAME) &&
				!data.channels.includes(MessageChannel.LINE_PROMOTION)
			) {
				return !!data.message && data.message.trim().length > 0
			}
			return true
		},
		{
			message: 'กรุณากรอกข้อความ',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			// Validate message length if provided
			if (data.message && data.message.length > 1000) {
				return false
			}
			return true
		},
		{
			message: 'ข้อความยาวเกินไป (สูงสุด 1000 ตัวอักษร)',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			if (data.sendType === MessageTransactionType.SCHEDULED) {
				return !!data.scheduledAt && data.scheduledAt > new Date()
			}
			return true
		},
		{
			message: 'กรุณาเลือกวันที่ในอนาคต',
			path: ['scheduledAt'],
		},
	)
	.refine(
		(data) => {
			// If SMS is selected, require provider and sender
			if (data.channels.includes(MessageChannel.SMS)) {
				return !!data.providerId && !!data.senderId
			}
			return true
		},
		{
			message: 'กรุณาเลือกผู้ให้บริการและชื่อผู้ส่ง SMS',
			path: ['providerId'],
		},
	)
	.refine(
		(data) => {
			// If LINE_HOT_GAME is selected, require hotGameFlexMessageId
			if (data.channels.includes(MessageChannel.LINE_HOT_GAME)) {
				return !!data.hotGameFlexMessageId
			}
			return true
		},
		{
			message: 'กรุณาเลือก Flex Message',
			path: ['hotGameFlexMessageId'],
		},
	)
	.refine(
		(data) => {
			// If LINE_PROMOTION is selected, require promotionId
			if (data.channels.includes(MessageChannel.LINE_PROMOTION)) {
				return !!data.promotionId
			}
			return true
		},
		{
			message: 'กรุณาเลือกโปรโมชั่น',
			path: ['promotionId'],
		},
	)

type MessageFormValues = z.infer<typeof messageFormSchema>

interface MessageFormProps {
	phoneNumbers: string[]
	lineIds: string[]
	playerCondition: GetPlayerByConditionRequest | null
	onSendSuccess?: () => void
}

export const MessageForm = ({
	phoneNumbers,
	lineIds,
	playerCondition,
	onSendSuccess,
}: MessageFormProps) => {
	const createMessageMutation = useMutationCreateMessage()
	const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false)
	const [isCopied, setIsCopied] = useState(false)

	const form = useForm<MessageFormValues>({
		resolver: zodResolver(messageFormSchema),
		defaultValues: {
			sendType: MessageTransactionType.IMMEDIATE,
			channels: [],
			providerId: '',
			senderId: '',
			message: '',
			scheduledAt: new Date(),
			hotGameFlexMessageId: '',
			promotionId: '',
			enableInstantClaim: false,
		},
	})

	const sendType = form.watch('sendType')
	const selectedChannels = form.watch('channels')

	useEffect(() => {
		if (sendType === MessageTransactionType.IMMEDIATE) {
			form.setValue('scheduledAt', undefined)
		}
	}, [sendType, form])

	const onSubmit = async (values: MessageFormValues) => {
		// Validate that we have recipients based on selected channels
		const hasRecipients =
			(values.channels.includes(MessageChannel.SMS) &&
				phoneNumbers.length > 0) ||
			(values.channels.includes(MessageChannel.LINE) && lineIds.length > 0) ||
			(values.channels.includes(MessageChannel.LINE_HOT_GAME) &&
				lineIds.length > 0) ||
			(values.channels.includes(MessageChannel.LINE_PROMOTION) &&
				lineIds.length > 0)

		if (!hasRecipients || !playerCondition) {
			toast.error('กรุณาค้นหาและเลือกผู้รับสำหรับช่องทางที่เลือก')
			return
		}

		// Only send phone numbers for SPECIFIC_PHONE_NUMBERS condition
		// For other conditions, backend will handle player fetching and batch sending
		const isSpecificPhoneNumbers =
			playerCondition.condition === MessageCondition.SPECIFIC_PHONE_NUMBERS

		try {
			await createMessageMutation.mutateAsync({
				senderId: values.senderId || '',
				message: values.message || '',
				// Only include phone numbers for specific phone numbers condition
				...(isSpecificPhoneNumbers && { playerPhoneNumber: phoneNumbers }),
				playerCondition,
				scheduleDate:
					values.scheduledAt?.toISOString() || new Date().toISOString(),
				type: values.sendType,
				channels: values.channels,
				hotGameFlexMessageId: values.hotGameFlexMessageId || undefined,
				promotionIds: values.promotionId ? [values.promotionId] : undefined,
				enableInstantClaim: values.enableInstantClaim || false,
			})

			toast.success('สร้างข้อความสำเร็จ')
			form.reset()
			onSendSuccess?.()
		} catch {
			toast.error('เกิดข้อผิดพลาดในการส่งข้อความ')
		}
	}

	const message = form.watch('message') || ''

	// Calculate SMS credits if SMS channel is selected
	let creditData: CreditData | null = null
	try {
		if (selectedChannels.includes(MessageChannel.SMS) && message) {
			creditData = calculateCredit(message)
		}
	} catch (error) {
		// Handle credit calculation errors (e.g., exceeds max credits)
		console.error('Credit calculation error:', error)
	}

	const phoneNumbersCommaSeparated = phoneNumbers.join(',')

	const handleCopyPhoneNumbers = async () => {
		try {
			await navigator.clipboard.writeText(phoneNumbersCommaSeparated)
			setIsCopied(true)
			toast.success('คัดลอกเบอร์โทรศัพท์แล้ว')
			setTimeout(() => setIsCopied(false), 2000)
		} catch {
			toast.error('ไม่สามารถคัดลอกได้')
		}
	}

	const handleDownloadCsv = () => {
		const csvContent = phoneNumbersCommaSeparated
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = 'phone_numbers.csv'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(url)
		toast.success('ดาวน์โหลดไฟล์ CSV สำเร็จ')
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>ตั้งค่าการส่งข้อความ</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
						className="space-y-6"
					>
						{/* Recipients info */}
						<div className="space-y-2">
							<div className="space-y-2">
								<span className="text-sm font-medium">
									ผู้รับที่เข้าเงื่อนไข
								</span>
								<div className="flex flex-wrap items-center gap-2">
									<Badge
										variant={phoneNumbers.length > 0 ? 'default' : 'secondary'}
									>
										SMS: {phoneNumbers.length} หมายเลข
									</Badge>
									{phoneNumbers.length > 0 && (
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => setIsPhoneModalOpen(true)}
										>
											<Eye className="mr-1 h-3 w-3" />
											ดูเบอร์โทร
										</Button>
									)}
									<Badge variant={lineIds.length > 0 ? 'default' : 'secondary'}>
										LINE: {lineIds.length} ID
									</Badge>
								</div>
							</div>
							{phoneNumbers.length === 0 && lineIds.length === 0 && (
								<p className="text-sm text-muted-foreground">
									กรุณาค้นหาและเลือกผู้รับก่อน
								</p>
							)}
						</div>

						{/* Send type */}
						<FormField
							control={form.control}
							name="sendType"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ประเภทการส่ง</FormLabel>
									<FormControl>
										<RadioGroup
											onValueChange={field.onChange}
											value={field.value}
											className="flex gap-4"
										>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value={MessageTransactionType.IMMEDIATE}
													id="immediate"
												/>
												<span className="cursor-pointer text-sm">ส่งทันที</span>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value={MessageTransactionType.SCHEDULED}
													id="scheduled"
												/>
												<span className="cursor-pointer text-sm">
													กำหนดเวลาส่ง
												</span>
											</div>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Scheduled date */}
						{sendType === MessageTransactionType.SCHEDULED && (
							<FormField
								control={form.control}
								name="scheduledAt"
								render={({ field }) => (
									<FormItem>
										<FormLabel>วันที่และเวลาที่ต้องการส่ง</FormLabel>
										<FormControl>
											<DateTimePicker
												date={field.value}
												onDateSelect={field.onChange}
												placeholder="เลือกวันที่และเวลา"
											/>
										</FormControl>
										<FormDescription>
											เลือกวันที่และเวลาที่ต้องการส่งข้อความ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{/* Channel selector */}
						<FormField
							control={form.control}
							name="channels"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<ChannelSelector
											value={field.value}
											onChange={field.onChange}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{/* Channel-specific settings */}
						{selectedChannels.includes(MessageChannel.SMS) && (
							<SMSSettingsForm form={form} />
						)}

						{selectedChannels.includes(MessageChannel.LINE) && (
							<LINESettingsForm />
						)}

						{/* Flex Message Selector for LINE_HOT_GAME */}
						{selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
							<FormField
								control={form.control}
								name="hotGameFlexMessageId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Flex Message</FormLabel>
										<FormControl>
											<FlexMessageSelector
												value={field.value}
												onChange={field.onChange}
												disabled={createMessageMutation.isPending}
											/>
										</FormControl>
										<FormDescription>
											เลือก Flex Message ที่ต้องการส่ง
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{/* Promotion Selector for LINE_PROMOTION */}
						{selectedChannels.includes(MessageChannel.LINE_PROMOTION) && (
							<>
								<FormField
									control={form.control}
									name="promotionId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>โปรโมชั่น</FormLabel>
											<FormControl>
												<PromotionSelector
													value={field.value}
													onChange={field.onChange}
													disabled={createMessageMutation.isPending}
													filterInstantClaimOnly={form.watch(
														'enableInstantClaim',
													)}
												/>
											</FormControl>
											<FormDescription>
												เลือกโปรโมชั่นที่ต้องการส่ง
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="enableInstantClaim"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={(checked) => {
														field.onChange(checked)
														// Reset promotion selection when toggling instant claim
														if (checked !== field.value) {
															form.setValue('promotionId', '')
														}
													}}
													disabled={createMessageMutation.isPending}
												/>
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel className="text-sm font-medium">
													ส่งลิงก์รับเครดิตฟรีทันที (ไม่ต้องฝาก)
												</FormLabel>
												<FormDescription>
													เมื่อเปิดใช้งาน
													ผู้รับจะได้ลิงก์สำหรับรับเครดิตฟรีทันที
													โดยไม่ต้องฝากเงิน (เฉพาะโปรโมชั่นที่รองรับ)
												</FormDescription>
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}

						{/* Message textarea - optional when using LINE_HOT_GAME or LINE_PROMOTION */}
						{!selectedChannels.includes(MessageChannel.LINE_HOT_GAME) &&
							!selectedChannels.includes(MessageChannel.LINE_PROMOTION) && (
								<FormField
									control={form.control}
									name="message"
									render={({ field }) => (
										<FormItem>
											<FormLabel>ข้อความ</FormLabel>
											<FormControl>
												<Textarea
													placeholder="กรอกข้อความที่ต้องการส่ง"
													className="min-h-[120px]"
													{...field}
												/>
											</FormControl>
											<FormDescription>
												{selectedChannels.includes(MessageChannel.SMS) && (
													<div className="space-y-1">
														{creditData ? (
															<>
																<div className="flex items-center justify-between text-sm">
																	<span>
																		{creditData.totalMessage -
																			creditData.messageLeft}{' '}
																		/ {creditData.totalMessage} ตัวอักษร (
																		{getLanguageLabel(creditData.lang)})
																	</span>
																	<span className="font-medium">
																		{creditData.credit} เครดิตต่อข้อความ
																	</span>
																</div>
															</>
														) : (
															<span className="text-sm">
																กรอกข้อความเพื่อดูจำนวนเครดิต SMS
															</span>
														)}
													</div>
												)}
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}

						{(selectedChannels.includes(MessageChannel.LINE_HOT_GAME) ||
							selectedChannels.includes(MessageChannel.LINE_PROMOTION)) && (
							<FormField
								control={form.control}
								name="message"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ข้อความเพิ่มเติม (ถ้ามี)</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรอกข้อความเพิ่มเติมที่ต้องการส่งพร้อมกับ Flex Message (ไม่บังคับ)"
												className="min-h-[120px]"
												{...field}
											/>
										</FormControl>
										<FormDescription>
											ข้อความนี้จะแสดงพร้อมกับ Flex Message (ถ้ากรอก)
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{/* Submit button */}
						<Button
							type="submit"
							className="w-full"
							disabled={
								createMessageMutation.isPending ||
								selectedChannels.length === 0 ||
								(phoneNumbers.length === 0 && lineIds.length === 0)
							}
						>
							{createMessageMutation.isPending ? (
								'กำลังสร้าง...'
							) : sendType === MessageTransactionType.SCHEDULED ? (
								<>
									<Calendar className="mr-2 h-4 w-4" />
									กำหนดเวลาส่ง
								</>
							) : (
								<>
									<Send className="mr-2 h-4 w-4" />
									ส่งข้อความ
								</>
							)}
						</Button>
					</form>
				</Form>
			</CardContent>

			{/* Phone Numbers Modal */}
			<Dialog open={isPhoneModalOpen} onOpenChange={setIsPhoneModalOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							เบอร์โทรศัพท์ผู้รับ ({phoneNumbers.length} หมายเลข)
						</DialogTitle>
						<DialogDescription>
							เบอร์โทรศัพท์ทั้งหมดที่เข้าเงื่อนไขการส่งข้อความ
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<Textarea
							readOnly
							value={phoneNumbersCommaSeparated}
							className="min-h-[200px] font-mono text-sm"
							placeholder="ไม่มีเบอร์โทรศัพท์"
						/>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								className="flex-1"
								onClick={handleCopyPhoneNumbers}
								disabled={phoneNumbers.length === 0}
							>
								{isCopied ? (
									<>
										<CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
										คัดลอกแล้ว
									</>
								) : (
									<>
										<Copy className="mr-2 h-4 w-4" />
										คัดลอกทั้งหมด
									</>
								)}
							</Button>
							<Button
								type="button"
								variant="outline"
								className="flex-1"
								onClick={handleDownloadCsv}
								disabled={phoneNumbers.length === 0}
							>
								<Download className="mr-2 h-4 w-4" />
								ดาวน์โหลด CSV
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</Card>
	)
}
